import create from "zustand";
import * as clsApi from "@/js/api/classroomApi";
import useStore from "@/stores/index";
import { checkForActiveMonitoringSession } from "@/utils/checkForActiveMonitoringSession";
import { ClassroomOverviewDTO, ModifySessionDTO } from "@/stores/classroomStore_I";

import { LocalisedMaxEndTime, fetchMaxEndTime, localiseMaxEndTime } from "@/utils/fetchMaxEndTime";
import { subscribeWithSelector } from "zustand/middleware";
import { useClassroomPolicyStore } from "./classroomPolicyStore";
import { useUserSessionStore } from "./userSessionStore";
import moment from "moment";

export interface ShowStartClassModalPayload {
	show: boolean;
	classroomName?: string;
	monitoring?: boolean;
}

export interface StartClassSessionPayload {
	classroomId: string;
	expiry: number;
	inClassroom?: boolean;
	router?: IComponentRouter;
	classroomName?: string;
	session?: { sessionEndTime: number };
	monitoringClass?: boolean;
	force?: boolean;
	group?: string;
}

export interface ShowEditClassModalPayload {
	show: boolean;
	classroomName?: string;
	classExpiryStr?: string;
	monitoring?: boolean;
}

export interface SetClassSessionErrorPayload {
	error: boolean;
}

export interface ShowMonitoringWarningModalPayload {
	group: string;
	currentMonitoring: ClassroomOverviewDTO;
	expiry?: number;
	session?: { sessionEndTime: number };
}

export interface ClassroomSessionState {
	selectedClassroomName: string | null;
	startClassSessionError: boolean;
	showStartClassModal: boolean;
	showEditClassModal: boolean;
	classExpiryStr: string | null;
	localisedMaxEndTime: LocalisedMaxEndTime | null;
	selectedClassroomMonitoring: boolean;
	showMonitoringClassActiveWarning: boolean;
	currentMonitoring: unknown;
	currentMonitoringClassName: boolean;
	savedClassExpiry: number | null;
	previousClassroomId: string | null;
	request: ModifySessionDTO | null;

	getLocalisedMaxEndTime: () => ClassroomSessionState["localisedMaxEndTime"];
	getSavedExpiry: () => ClassroomSessionState["savedClassExpiry"];

	handleShowStartClassModal: (payload: ShowStartClassModalPayload) => void;
	handleShowMonitoringWarningModal: (payload: ShowMonitoringWarningModalPayload) => void;
	handleShowEditClassModal: (payload: ShowEditClassModalPayload) => void;
	handleStartClassSession: (payload: StartClassSessionPayload) => Promise<void>;
	startMonitoringSession: (payload: StartClassSessionPayload, classExpiry: number) => Promise<void>;
	handleSetClassSessionError: (payload: SetClassSessionErrorPayload) => void;
	fetchAndPopulateMaxEndTime: () => void;
	handleCloseMonitoringWarningModal: () => void;
}

export const useClassroomSessionStore = create(
	subscribeWithSelector<ClassroomSessionState>((set, get) => ({
		selectedClassroomName: null,
		startClassSessionError: false,
		showStartClassModal: false,
		showEditClassModal: false,
		classExpiryStr: null,
		localisedMaxEndTime: null,
		selectedClassroomMonitoring: false,
		showMonitoringClassActiveWarning: false,
		currentMonitoring: {},
		currentMonitoringClassName: false,
		savedClassExpiry: null,
		previousClassroomId: null,
		request: null,

		getLocalisedMaxEndTime: () => {
			return get().localisedMaxEndTime;
		},

		getSavedExpiry: () => {
			return get().savedClassExpiry;
		},

		handleShowStartClassModal: (payload) => {
			set((state) => ({
				...state,
				showStartClassModal: payload.show,
				selectedClassroomName: payload.classroomName ?? state.selectedClassroomName,
				savedClassExpiry: null,
				startClassSessionError: false,
				selectedClassroomMonitoring: typeof payload.monitoring !== "undefined" ? payload.monitoring : state.selectedClassroomMonitoring,
			}));
		},

		handleShowMonitoringWarningModal: (payload) => {
			set((state) => ({
				...state,
				showMonitoringClassActiveWarning: true,
				currentMonitoring: payload.currentMonitoring,
				selectedClassroomName: payload.group,
				savedClassExpiry: payload.expiry ?? payload.session?.sessionEndTime ?? null,
				startClassSessionError: false,
				showStartClassModal: false,
				showEditClassModal: false,
				selectedClassroomMonitoring: true,
			}));
		},

		handleShowEditClassModal: (payload) => {
			set((state) => ({
				...state,
				showEditClassModal: payload.show,
				selectedClassroomName: payload.classroomName ?? state.selectedClassroomName,
				classExpiryStr: payload.classExpiryStr ?? null,
				savedClassExpiry: null,
				selectedClassroomMonitoring: typeof payload.monitoring !== "undefined" ? payload.monitoring : state.selectedClassroomMonitoring,
			}));
		},

		handleStartClassSession: async (payload) => {
			const { selectedClassroomName, selectedClassroomMonitoring } = get();
			let expiry = payload.expiry;

			if (payload.session) {
				expiry = payload.session.sessionEndTime;
			}

			const { router } = payload;
			set({ startClassSessionError: false });

			const classExpiry = expiry === 0 ? 0 : Math.floor(Date.now() / 1000 + expiry);

			if (selectedClassroomMonitoring) {
				await get().startMonitoringSession(payload, classExpiry);
				return;
			}

			const storeClassroomName = payload.classroomName ?? selectedClassroomName;

			const handleClassStartedSuccess = () => {
				const requestInState = get().request;
				if (!requestInState) return;

				useStore.getState().classroom.updateClassroomZoomV2(requestInState);
				useStore.getState().classroom.updateClassroomStartTime(requestInState.classroomId, moment().unix());

				// Close modals and clear request
				get().handleShowStartClassModal({ show: false });
				get().handleShowEditClassModal({ show: false });
				set({ request: null });

				if (classExpiry !== 0) {
					if (useUserSessionStore.getState().getActiveGroup() !== storeClassroomName || !payload.inClassroom) {
						useUserSessionStore.getState().setActiveClassroom(storeClassroomName as string);
					}
				} else {
					if (router) {
						router.navigate("/classrooms/overview");
					}
				}

				useStore.getState().activeClassroomWindow.setClassroomExpiry(storeClassroomName as string, classExpiry);

				// Refresh policy details in Classroom Policy Store
				void useClassroomPolicyStore.getState().fetchPolicyAndPopulateStore({});
			};

			const handleClassStartedError = (error: unknown) => {
				console.log("Error starting class session", error);
				get().handleSetClassSessionError({ error: true });
			};

			const classroom = useStore.getState().classroom.getClassroomV2(payload.classroomId);
			const teacherId = useStore.getState().classroom.getTeacherStableIdV1();
			const classroomStableIdV1 = classroom.stableIdV1;

			const requestBody = {
				policy_id: classroomStableIdV1,
				classroomId: payload.classroomId,
				classroomSourceId: storeClassroomName,
				name: classroom.name,
				expiryUnix: classExpiry,
				isMonitoringMode: false,
				teacherId,
				classroomIdV1: classroomStableIdV1,
			};

			if (!classroomStableIdV1) {
				console.error("Policy ID is missing in the start class request", requestBody);
			}

			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore: Ignoring for now
			set({ request: requestBody });

			await clsApi.Post("classrooms/session/regular", requestBody, handleClassStartedSuccess, handleClassStartedError);
		},

		startMonitoringSession: async (payload, classExpiry) => {
			const { router } = payload;
			let force = false;
			const hideMonitorWarning = localStorage.getItem("hideMonitoringSessionWarning") === "true";

			if ("force" in payload) {
				force = payload.force ?? false;
			}

			const callback = async () => {
				const storeClassroomName = payload.classroomName || payload.group || get().selectedClassroomName;

				const handleClassStartedSuccess = () => {
					const req = get().request;
					if (req) {
						useStore.getState().classroom.updateMonitorClassroomDataV2(req);
					}

					set({
						showStartClassModal: false,
						showMonitoringClassActiveWarning: false,
						showEditClassModal: false,
						request: null,
					});

					get().handleCloseMonitoringWarningModal();

					if (classExpiry !== 0) {
						if (useUserSessionStore.getState().getActiveGroup() !== storeClassroomName || !payload.inClassroom) {
							useUserSessionStore.getState().setActiveClassroom(storeClassroomName as string);
						}
					} else {
						if (router) {
							router.navigate("/classrooms/overview");
						}
					}

					useStore.getState().activeClassroomWindow.setClassroomExpiry(storeClassroomName as string, classExpiry);
				};

				const handleClassStartedError = (error: unknown) => {
					console.log("Error starting monitoring session", error);
					get().handleSetClassSessionError({ error: true });
				};

				const classroom = useStore.getState().classroom.getClassroomV2(payload.classroomId);
				const teacherId = useStore.getState().classroom.getTeacherStableIdV1();
				const classroomStableIdV1 = classroom.stableIdV1;

				const requestV2 = {
					classroomId: payload.classroomId,
					classroomSourceId: storeClassroomName,
					expiryUnix: classExpiry,
					isMonitoringMode: true,
					name: classroom.name,
					teacherId,
					classroomIdV1: classroomStableIdV1,
				};

				if (force || hideMonitorWarning) {
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore: Ignoring for now
					requestV2.previousClassroomId = get().previousClassroomId;
				}

				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore: Ignoring for now
				set({ request: requestV2 });

				await clsApi.Post("classrooms/session/monitor", requestV2, handleClassStartedSuccess, handleClassStartedError);
			};

			if (force || hideMonitorWarning) {
				await callback();
			} else {
				const activeMonitoringClassroom = checkForActiveMonitoringSession();
				const storeClassroomName = get().selectedClassroomName;

				if (activeMonitoringClassroom && activeMonitoringClassroom.sourceId !== storeClassroomName) {
					// Another class is active in monitoring
					set({ previousClassroomId: activeMonitoringClassroom.id });
					get().handleShowMonitoringWarningModal({
						group: storeClassroomName ?? "",
						currentMonitoring: activeMonitoringClassroom,
						expiry: payload.expiry,
					});
				} else {
					// Continue if no other active monitoring classes
					set({ showMonitoringClassActiveWarning: false });
					void callback();
				}
			}
		},

		handleSetClassSessionError: (payload) => {
			set({ startClassSessionError: payload.error });
		},

		fetchAndPopulateMaxEndTime: () => {
			fetchMaxEndTime()
				.then((maxEndTime) => {
					if (!maxEndTime) {
						set({ localisedMaxEndTime: null });
						return;
					}
					const localisedMaxEndTime = localiseMaxEndTime(maxEndTime);
					set({ localisedMaxEndTime });
				})
				.catch((error) => {
					console.error("Error fetching max end time", error);
				});
		},

		handleCloseMonitoringWarningModal: () => {
			set({ showMonitoringClassActiveWarning: false });
		},
	}))
);
